import { useState, useCallback } from "react";
import { KYCService } from "../utils/kycService";

export const useKYC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Submit KYC application (only database operation)
  const submitKYCApplication = useCallback(async (formData) => {
    setLoading(true);
    setError(null);

    try {
      // Add user context data
      const enrichedData = {
        ...formData,
        userAgent: navigator.userAgent,
        ipAddress: null, // Could be added if IP tracking is needed
      };

      const result = await KYCService.createKYCApplication(enrichedData);

      if (result.error) {
        setError(result.error);
        return { success: false, error: result.error };
      }

      return {
        success: true,
        data: result.data,
        sessionId: result.sessionId,
      };
    } catch (err) {
      const errorMessage = "Failed to submit KYC application";
      setError(errorMessage);
      console.error("Error submitting KYC:", err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get KYC application by ID (admin/review purposes)
  const getKYCApplication = useCallback(async (applicationId) => {
    setLoading(true);
    setError(null);

    try {
      const result = await KYCService.getKYCApplication(applicationId);

      if (result.error) {
        setError(result.error);
        return { success: false, error: result.error };
      }

      return { success: true, data: result.data };
    } catch (err) {
      const errorMessage = "Failed to load KYC application";
      setError(errorMessage);
      console.error("Error loading KYC application:", err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Get all KYC applications (admin purposes)
  const getAllKYCApplications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await KYCService.getAllKYCApplications();

      if (result.error) {
        setError(result.error);
        return { success: false, error: result.error };
      }

      return { success: true, data: result.data };
    } catch (err) {
      const errorMessage = "Failed to load KYC applications";
      setError(errorMessage);
      console.error("Error loading KYC applications:", err);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update application status (admin only)
  const updateApplicationStatus = useCallback(
    async (applicationId, newStatus, adminNotes) => {
      setLoading(true);
      setError(null);

      try {
        const result = await KYCService.updateApplicationStatus(
          applicationId,
          newStatus,
          adminNotes
        );

        if (result.error) {
          setError(result.error);
          return { success: false, error: result.error };
        }

        return { success: true, data: result.data };
      } catch (err) {
        const errorMessage = "Failed to update application status";
        setError(errorMessage);
        console.error("Error updating application status:", err);
        return { success: false, error: errorMessage };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,

    // Form submission
    submitKYCApplication,

    // Admin functions
    getKYCApplication,
    getAllKYCApplications,
    updateApplicationStatus,

    // Utility
    clearError,
  };
};
